<template>
  <div>
    <div class="card" ref="chartCard">
      <b-skeleton-wrapper :loading="isLoading">
        <template #loading>
          <div class="card-body">
            <b-skeleton width="85%"></b-skeleton>
            <b-skeleton width="55%"></b-skeleton>
            <b-skeleton width="70%"></b-skeleton>
          </div>
        </template>
        <div class="card-body">
          <div class="row mb-4">
            <div class="col-md-10">
              <div class="d-flex flex-wrap">
                <div>
                  <h5 class="mb-1">{{ cardTitle }}</h5>
                  <p class="mb-0 font-size-10">
                    {{ ranged ? `${periodLabel[0]} - ${periodLabel[1]}` : monthlyLabel }}
                    <span v-if="hasConfig">
                      {{ `, ${fleetCategory.length} ${$t('global.field.vehicleType')}` }}
                    </span>
                  </p>
                </div>
              </div>
            </div>
            <div class="col-md-2 d-flex justify-content-end">
              <div class="mc-table-filter">
                <div class="mc-table-view">
                  <b-dropdown dropleft variant="white">
                    <template v-slot:button-content>
                      <i class="mc-fa icon-outline_more-vertical"></i>
                    </template>
                    <b-dropdown-item @click="openDatepicker()">{{
                      $t('global.object.period')
                    }}</b-dropdown-item>
                    <b-dropdown-item v-if="hasConfig" @click="openModal()">
                      <i class="mc-fa icon-outline_settings"></i>
                      {{ $t('global.object.configuration') }}
                    </b-dropdown-item>
                    <mc-date-picker
                      v-show="isShow"
                      v-if="ranged"
                      type="month"
                      v-model="period"
                      format="MMM YYYY"
                      value-type="YYYY-MM-DD"
                      :placeholder="$t('report.object.reportPeriod')"
                      @pick="pickDate"
                      @close="closePicker"
                      :confirm="true"
                      :draft="true"
                      :confirm-text="$t('global.action.apply')"
                      range-separator=" - "
                      range
                      ref="periodMonthPicker"
                      :popup-style="{
                        position: 'fixed',
                        top: '50%',
                        left: '0',
                        right: '0',
                        margin: '0 auto',
                        padding: '20px',
                        width: 'fit-content',
                        transform: 'translateY(-50%)',
                      }"
                      :disabled-date="disabledRange"
                      @input="applyFilter"
                      :lang="datePickerLang"
                    >
                      <template #header>
                        <h5 class="text-center mc-general__label">
                          {{ `${$t('global.object.period')} ${cardTitle}` }}
                        </h5>
                      </template>
                      <template #footer>
                        <b-button class="mx-2" variant="outline-primary" @click="closeCalendar()">{{
                          $t('global.action.cancel')
                        }}</b-button>
                      </template>
                    </mc-date-picker>
                    <mc-date-picker
                      v-show="isShow"
                      v-else
                      type="month"
                      v-model="periodMonthly"
                      format="MMM YYYY"
                      value-type="YYYY-MM-DD"
                      :placeholder="$t('report.object.reportPeriod')"
                      @pick="pickDate"
                      @close="closePicker"
                      :confirm="true"
                      :draft="true"
                      :confirm-text="$t('global.action.apply')"
                      ref="periodMonthPicker"
                      :popup-style="{
                        position: 'fixed',
                        top: '50%',
                        left: '0',
                        right: '0',
                        margin: '0 auto',
                        padding: '20px',
                        width: 'fit-content',
                        transform: 'translateY(-50%)',
                      }"
                      :disabled-date="disabledRange"
                      @input="applyFilter"
                      :lang="datePickerLang"
                    >
                      <template #header>
                        <h5 class="text-center mc-general__label">
                          {{ `${$t('global.object.period')} ${cardTitle}` }}
                        </h5>
                      </template>
                      <template #footer>
                        <b-button class="mx-2" variant="outline-primary" @click="closeCalendar()">{{
                          $t('global.action.cancel')
                        }}</b-button>
                      </template>
                    </mc-date-picker>
                  </b-dropdown>
                </div>
              </div>
            </div>
          </div>
          <div>
            <BarChart
              :labels="chartData.labels"
              :datasets="chartData.datasets"
              :yLabel="yLabel"
              :barLabel="withLabel"
              :options="{
                legend: {
                  position: withLabel ? 'bottom' : 'top',
                },
              }"
              :labelColor="color"
            ></BarChart>
          </div>
        </div>
      </b-skeleton-wrapper>
    </div>
    <b-modal :id="ids" :title="$t('dashboard.title.fuelUsageConfiguration')" size="md" centered>
      <b-form-group
        id="fleet-category"
        :label="$t('global.field.vehicleType')"
        label-for="category-option"
      >
        <multiselect
          id="category-option"
          v-model="fleetCategory"
          :options="categoryOptions"
          track-by="vehicleTypeId"
          label="label"
          :placeholder="$t('global.field.vehicleType')"
          :multiple="true"
          :taggable="true"
        >
          <template slot="selection" slot-scope="{ values, isOpen }">
            <div class="multiselect__tags-wrap" v-if="values.length && !isOpen">
              <span class="multiselect__tag">
                <span>{{ values[0].label }}</span>
                <i aria-hidden="true" tabindex="1" class="multiselect__tag-icon"></i>
              </span>
              <span
                class="badge bg-success px-2 py-1 d-inline-block"
                v-if="values.length > 1 && !isOpen"
                >+{{ values.length - 1 }}</span
              >
            </div>
          </template>
        </multiselect>
      </b-form-group>
      <b-form-group
        v-if="ids == 'monthlyComparation'"
        id="y-label"
        :label="$t('dashboard.text.yAxes')"
        label-for="ylabel-option"
      >
        <multiselect
          id="ylabel-option"
          v-model="yLabelSelected"
          :options="yLabelOption"
          track-by="key"
          label="label"
          :placeholder="$t('dashboard.text.yAxes')"
        ></multiselect>
      </b-form-group>
      <template #modal-footer="{ cancel }">
        <div class="w-100">
          <a class="text-primary float-left" @click="resetFilter">{{
            $t('global.action.reset')
          }}</a>
          <div class="d-flex float-end">
            <McButton
              variant="secondary"
              class="me-2"
              :text="$t('global.action.cancel')"
              @click="cancel()"
            />
            <McButton variant="primary" :text="$t('global.action.apply')" @click="applyFilter" />
          </div>
        </div>
      </template>
    </b-modal>
  </div>
</template>
<script>
import { Helper } from '@/mixin/helper'
import BarChart from '@/components/Chart/Bar.vue'
import Multiselect from 'vue-multiselect'
import McButton from '@/components/McButton'
import { GET_VEHICLE_TYPE } from '@/services/api/setting/fleet/fleetType'
import moment from 'moment'
import datePickerLang from '@/helpers/date-picker-lang'
import McDatePicker from '@/components/McDatePicker'

moment.locale('id')

export default {
  components: { BarChart, Multiselect, McButton, McDatePicker },
  mixins: [Helper],
  apollo: {
    vehicleTypes: {
      fetchPolicy: 'no-cache',
      query: GET_VEHICLE_TYPE,
      variables() {
        return {
          filter: {
            hasFuelSensor: true,
          },
        }
      },
      result({ data, loading }) {
        if (!loading) {
          this.categoryOptions = data.vehicleTypes.vehicleTypes.data.map((item) => {
            return {
              vehicleTypeId: item.vehicleTypeId,
              label: `${item.model}/${item.type}`,
            }
          })
          this.fleetCategory = this.categoryOptions
        }
      },
    },
  },
  props: {
    isLayoutEdit: { type: Boolean, default: false },
    cardTitle: { type: String, required: true },
    withLabel: { type: Boolean, default: false },
    yLabel: { type: String, required: true },
    ids: { type: String, required: true },
    hasConfig: { type: Boolean, default: false },
    ranged: { type: Boolean, default: true },
    color: { type: String, default: 'black' },
  },
  data() {
    return {
      isLoading: false,
      isShow: false,
      period: [moment().subtract(5, 'months').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')],
      periodMonthly: moment().format('YYYY-MM-DD'),
      periodLabel: [],
      monthlyLabel: '',
      pastDate: Helper.methods.momentAdd(new Date(), -6, 'M'),
      futureEnd: moment().add(1, 'years'),
      fleetCategory: [],
      categoryOptions: [],
      yLabelSelected: { key: 'F', label: this.$t('dashboard.text.litre') },
      yLabelOption: [
        { key: 'F', label: this.$t('dashboard.text.litre') },
        { key: 'C', label: this.$t('dashboard.text.rupiah') },
      ],
      chartData: {
        labels: [],
        datasets: [],
      },
    }
  },
  computed: {
    datePickerLang() {
      return datePickerLang()
    },
  },
  methods: {
    openDatepicker() {
      this.$refs.periodMonthPicker.openPopup()
    },
    closeCalendar() {
      this.$refs.periodMonthPicker.cancel()
    },
    disabledRange: function (date) {
      return date <= this.pastDate || date >= this.futureEnd || date > new Date()
    },
    pickDate(date) {
      let maxDate = 180

      // Get the current date and time
      let currentDate = new Date()

      if (new Date(date.getTime() + 24 * 0 * 3600 * 1000).getTime() === currentDate.getTime()) {
        this.futureEnd = new Date(date.getTime() + 24 * 0 * 3600 * 1000)
      } else {
        this.futureEnd = new Date(date.getTime() + 24 * maxDate * 3600 * 1000)
      }
    },
    closePicker() {
      this.pastDate = this.momentAdd(new Date(), -6, 'M')
      this.futureEnd = moment().add(1, 'years')
    },
    openModal() {
      this.$bvModal.show(this.ids)
    },
    async initLocalStorage(configName, vgroupId) {
      await this.$apollo.queries.vehicleTypes.refetch({
        filter: {
          hasFuelSensor: true,
          vgroupId,
        },
      })
      const startDate = this.ranged
        ? moment(this.period[0]).format('YYYY-MM')
        : moment(this.periodMonthly).format('YYYY-MM')
      const endDate = this.ranged
        ? moment(this.period[1]).format('YYYY-MM')
        : moment(this.periodMonthly).format('YYYY-MM')
      const filter = {
        startDate: startDate,
        endDate: endDate,
        vehicleTypeId: this.fleetCategory.map((item) => item.vehicleTypeId),
      }
      if (this.ids == 'monthlyComparation') {
        filter.yLabel = this.yLabelSelected
      }
      localStorage.setItem(configName, JSON.stringify(filter))
    },
    async loadLocalStorage(data, vgroupId) {
      await this.$apollo.queries.vehicleTypes.refetch({
        filter: {
          hasFuelSensor: true,
          vgroupId,
        },
      })
      this.period[0] = this.momentYmD(data.startDate)
      this.period[1] = this.momentYmD(data.endDate)
      this.fleetCategory = this.categoryOptions.filter((item) =>
        data.vehicleTypeId.includes(item.vehicleTypeId)
      )
      if (this.ids == 'monthlyComparation') {
        this.yLabelSelected = data.yLabel
      }
      if (this.ids == 'totalFleet') {
        this.periodMonthly = this.momentYmD(data.endDate)
      }
    },
    resetFilter() {
      this.fleetCategory = this.categoryOptions
      if (this.ids == 'monthlyComparation') {
        this.yLabelSelected = { key: 'F', label: this.$t('dashboard.text.litre') }
      }
      this.applyFilter()
    },
    applyFilter() {
      const startDate = this.ranged
        ? moment(this.period[0]).format('YYYY-MM')
        : moment(this.periodMonthly).format('YYYY-MM')
      const endDate = this.ranged
        ? moment(this.period[1]).format('YYYY-MM')
        : moment(this.periodMonthly).format('YYYY-MM')
      const filter = {
        startDate: startDate,
        endDate: endDate,
        vehicleTypeId: this.fleetCategory.map((item) => item.vehicleTypeId),
      }
      if (this.ids == 'monthlyComparation') {
        filter.yLabel = this.yLabelSelected
      }
      localStorage.setItem(`${this.ids}-config`, JSON.stringify(filter))
      this.$emit('filter', filter)
      this.$bvModal.hide(this.ids)
    },
    initData() {
      this.isLoading = true
      this.periodLabel[0] = moment(this.period[0]).format('MMM YYYY')
      this.periodLabel[1] = moment(this.period[1]).format('MMM YYYY')
      if (this.ids == 'totalFleet') {
        this.monthlyLabel = moment(this.periodMonthly).format('MMM YYYY')
      }
      setTimeout(() => {
        this.isLoading = false
      }, 800)
    },
  },
}
</script>
